/**
 * Safety layer — anchor ring, MOB mark, danger look-ahead cone, dangerous-
 * feature highlights. Lives on top of the chart, below the chartwork plots.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import {
  anchorRing,
  lookaheadCone,
  type AnchorWatch,
  type DangerHit,
  type MobMark,
  type SafetySettings,
} from '@/safety';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'safety';

interface SafetyInput {
  anchor: AnchorWatch | null;
  mob: MobMark | null;
  dangers: DangerHit[];
  ownship: { position: [number, number] | null; cog: number; sog: number };
  settings: SafetySettings;
}

function build(input: SafetyInput): FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (input.anchor) {
    features.push(anchorRing(input.anchor));
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: input.anchor.centre },
      properties: { kind: 'anchor-centre' },
    });
  }
  if (input.mob) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: input.mob.position },
      properties: { kind: 'mob' },
    });
  }
  if (input.ownship.position && input.ownship.sog > 0) {
    const cone = lookaheadCone(
      input.ownship.position,
      input.ownship.cog,
      input.ownship.sog,
      input.settings,
    );
    if (cone) features.push(cone);
  }
  for (const d of input.dangers) {
    const point = featureFocalPoint(d.feature.geometry);
    if (!point) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: point },
      properties: { kind: 'danger', reason: d.reason, rangeNm: d.rangeNm },
    });
  }
  return { type: 'FeatureCollection', features };
}

function featureFocalPoint(geom: GeoJSON.Geometry): [number, number] | null {
  switch (geom.type) {
    case 'Point':
      return [geom.coordinates[0], geom.coordinates[1]];
    case 'LineString': {
      const c = geom.coordinates;
      return c.length ? [c[Math.floor(c.length / 2)][0], c[Math.floor(c.length / 2)][1]] : null;
    }
    case 'Polygon': {
      const ring = geom.coordinates[0];
      if (!ring?.length) return null;
      let sx = 0;
      let sy = 0;
      for (const p of ring) {
        sx += p[0];
        sy += p[1];
      }
      return [sx / ring.length, sy / ring.length];
    }
    default:
      return null;
  }
}

export function ensureSafetyLayer(map: MaplibreMap, input: SafetyInput, palette: Palette): void {
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build(input) });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(build(input));
  }

  if (!map.getLayer('safety-anchor-fill')) {
    map.addLayer({
      id: 'safety-anchor-fill',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'anchor-ring'],
      paint: {
        'fill-color': p.UI_ACCENT,
        'fill-opacity': 0.08,
      },
    });
  }
  if (!map.getLayer('safety-anchor-stroke')) {
    map.addLayer({
      id: 'safety-anchor-stroke',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'anchor-ring'],
      paint: { 'line-color': p.UI_ACCENT, 'line-width': 1.2, 'line-dasharray': [3, 2] },
    });
  }
  if (!map.getLayer('safety-anchor-centre')) {
    map.addLayer({
      id: 'safety-anchor-centre',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'anchor-centre'],
      // A drawn icon, not the character U+2693, which the glyph stack does not
      // carry and so drew nothing at all. See 'plot-anchor' in icons.ts.
      layout: {
        'icon-image': 'plot-anchor',
        'icon-size': 0.85,
        'icon-allow-overlap': true,
      },
    });
  }
  if (!map.getLayer('safety-mob')) {
    map.addLayer({
      id: 'safety-mob',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'mob'],
      layout: {
        // The perry-buoy (ring lifebuoy) icon, the same one the MOB button drops,
        // so the on-chart mark reads as exactly that. A small 'MOB' label sits just
        // under it so the mark is still named at a glance.
        'icon-image': 'plot-mob',
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'text-field': 'MOB',
        'text-font': ['Open Sans Regular'],
        'text-size': 12,
        'text-offset': [0, 1.4],
        'text-anchor': 'top',
        'text-allow-overlap': true,
      },
      paint: { 'text-color': '#ffffff', 'text-halo-color': p.CHMGD, 'text-halo-width': 2.4 },
    });
  }
  if (!map.getLayer('safety-cone')) {
    map.addLayer({
      id: 'safety-cone',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'lookahead'],
      paint: { 'fill-color': p.CHMGD, 'fill-opacity': 0.06 },
    });
  }
  if (!map.getLayer('safety-danger')) {
    map.addLayer({
      id: 'safety-danger',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'danger'],
      paint: {
        'circle-radius': 9,
        'circle-color': 'transparent',
        'circle-stroke-color': p.CHMGD,
        'circle-stroke-width': 2,
      },
    });
  }
}

export function updateSafetyLayer(map: MaplibreMap, input: SafetyInput): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(build(input));
}
